import { useState, useEffect, useMemo, useCallback } from 'react';
import './corporations-page.css';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import Pagination from '../../../components/v2/Pagination.jsx';
import CorporationsTable, { CORP_COLUMN_DEFS } from './components/CorporationsTable.jsx';
import CorporationDrawer from './components/CorporationDrawer.jsx';
// Shared components from residents (fully generic)
import ActiveFilterBar from '../../../components/v2/ActiveFilterBar.jsx';
import Toast from '../../../components/v2/Toast.jsx';
import useToast from '../../../hooks/useToast.js';
import ColumnVisibilityToggle from '../../../components/v2/ColumnVisibilityToggle.jsx';
import TableSkeleton from '../../../components/v2/TableSkeleton.jsx';
// V2 data layer
import {
  getCampusId, getUserId, getSessionId, getMyRoleWeight,
} from '../../../services/session.js';
import {
  fetchCorporations, fetchUserRoles, addCorporation,
} from './api/corporationsApi.js';
import { exportCorporationsCsv } from '../../../services/csvExport.js';
import usePersistedColumns from '../../../hooks/usePersistedColumns.js';
import usePersistedPerPage from '../../../hooks/usePersistedPerPage.js';
import { iAmHigherAuthority as iAmHigherAuth } from '../../../libs.jsx';
import { impersonationRoles, startImpersonation } from '../../../utils/impersonation.js';
import ImpersonateDialog from '../../../components/v2/ImpersonateDialog.jsx';

/** All column keys */
const ALL_COLUMN_KEYS = CORP_COLUMN_DEFS.map((c) => c.key);

/** All columns visible by default (only 2 columns) */
const DEFAULT_VISIBLE = [...ALL_COLUMN_KEYS];

/**
 * CorporationsPage — V2 cross-corporation admin overview.
 *
 * Read + Add, with checkboxes for bulk export, and Impersonate
 * in the per-row 3-dot kebab menu (only when the corp has campuses).
 */
const CorporationsPage = () => {
  // ==========================================================================
  // SESSION
  // ==========================================================================
  const campusId = useMemo(() => getCampusId(), []);
  /* The user's OWN weight (my_role_weight), not the impersonated role_weight: the same weight
     Customers.jsx gates this page on (> 80). Impersonating into a community as a lower role changes role_weight,
     which used to take away this page's rights while the page itself stayed open. */
  const myRoleWeight = useMemo(() => getMyRoleWeight(), []);
  const addRights = useMemo(() => iAmHigherAuth(80, myRoleWeight), [myRoleWeight]);

  /** Builds the session params object used by API calls */
  const getSessionParams = useCallback(() => ({
    campusId,
    userId: getUserId(),
    clientId: getSessionId(),
  }), [campusId]);

  // ==========================================================================
  // TOAST
  // ==========================================================================
  const { toast, showToast, dismissToast } = useToast(6000);

  // ==========================================================================
  // DATA STATE
  // ==========================================================================
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});
  const [sortColumn, setSortColumn] = useState('corporate_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = usePersistedPerPage();
  const [visibleColumns, handleColumnToggle] = usePersistedColumns('corporations', DEFAULT_VISIBLE, ALL_COLUMN_KEYS);
  const [selectedIds, setSelectedIds] = useState([]);

  // Drawer + support data
  const [userRoles, setUserRoles] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Impersonate
  const [impersonateTarget, setImpersonateTarget] = useState(null);
  const [impersonateRoleWeight, setImpersonateRoleWeight] = useState(myRoleWeight);
  const [impersonating, setImpersonating] = useState(false);

  // ==========================================================================
  // DATA LOADING
  // ==========================================================================
  const loadCorporations = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchCorporations(campusId, true);
      setData(result || []);
    } catch (err) {
      console.error('[CorporationsPage] Load corporations error:', err);
      showToast('error', 'Failed to load corporations');
    } finally {
      setLoading(false);
    }
  }, [campusId, showToast]);

  const loadUserRoles = useCallback(async () => {
    try {
      const result = await fetchUserRoles(campusId);
      setUserRoles(result || []);
    } catch (err) {
      console.error('[CorporationsPage] Load user roles error:', err);
    }
  }, [campusId]);

  useEffect(() => {
    loadCorporations();
    loadUserRoles();
  }, [loadCorporations, loadUserRoles]);

  // ==========================================================================
  // FILTER / SORT PIPELINE
  // ==========================================================================
  const filtered = useMemo(() => {
    let result = [...data];

    // 1. Text search
    if (search.trim()) {
      const s = search.toLowerCase();
      result = result.filter((r) =>
        r.corporate_name?.toLowerCase().includes(s) ||
        r.campus_name?.toLowerCase().includes(s)
      );
    }

    // 2. Column filters
    Object.entries(filters).forEach(([columnKey, values]) => {
      if (!values || values.length === 0) return;
      result = result.filter((row) => {
        const cellVal = row[columnKey];
        return values.includes(cellVal != null && cellVal !== '' ? String(cellVal) : '(empty)');
      });
    });

    // 3. Sort
    result.sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
      return sortOrder === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [data, search, filters, sortColumn, sortOrder]);

  // ==========================================================================
  // PAGINATION
  // ==========================================================================
  const totalFiltered = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / perPage));
  const safePage = Math.min(page, totalPages);

  const paginated = useMemo(() => {
    const start = (safePage - 1) * perPage;
    return filtered.slice(start, start + perPage);
  }, [filtered, safePage, perPage]);

  useEffect(() => { setPage(1); }, [search, filters, perPage]);

  /** Column labels for ActiveFilterBar */
  const columnLabels = useMemo(() => {
    const map = {};
    CORP_COLUMN_DEFS.forEach((c) => { map[c.key] = c.label; });
    return map;
  }, []);

  // ==========================================================================
  // HANDLERS
  // ==========================================================================
  const handleSort = useCallback((key) => {
    setSortColumn((prev) => {
      if (prev === key) {
        setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortOrder('asc');
      return key;
    });
  }, []);

  const handleFilterChange = useCallback((columnKey, selectedValues) => {
    setFilters((prev) => ({ ...prev, [columnKey]: selectedValues }));
  }, []);

  const handleRemoveFilter = useCallback((columnKey) => {
    setFilters((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setFilters({});
  }, []);

  // Selection
  const handleSelectRow = useCallback((id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    const allIds = paginated.map((r) => r.corporate_id);
    const allSelected = allIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !allIds.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...allIds])]);
    }
  }, [paginated, selectedIds]);

  // Export
  const handleExportAll = useCallback(() => {
    if (filtered.length === 0) {
      showToast('error', 'No data to export');
      return;
    }
    exportCorporationsCsv(filtered, 'corporations.csv');
    showToast('success', `Exported ${filtered.length} corporations`);
  }, [filtered, showToast]);

  const handleBulkExport = useCallback(() => {
    const selected = data.filter((d) => selectedIds.includes(d.corporate_id));
    if (selected.length) {
      exportCorporationsCsv(selected, 'corporations-selected.csv');
      showToast('success', `Exported ${selected.length} corporations`);
    }
  }, [selectedIds, data, showToast]);

  // Add Corporation
  const handleAddCorporation = useCallback(async (formData) => {
    const session = getSessionParams();
    const [success, errMsg] = await addCorporation(formData, session);
    if (success) {
      showToast('success', `${formData.corporate_name} has been added`);
      setDrawerOpen(false);
      loadCorporations();
    } else {
      showToast('error', `Failed to add corporation: ${errMsg || 'Unknown error'}`);
    }
  }, [getSessionParams, showToast, loadCorporations]);

  // Impersonate
  const handleImpersonate = useCallback((corporation) => {
    setImpersonateTarget(corporation);
    setImpersonateRoleWeight(myRoleWeight);
    setImpersonating(false);
  }, [myRoleWeight]);

  const handleConfirmImpersonate = useCallback(() => {
    if (!impersonateTarget) return;
    // A corporation: every campus of the corp, the first one selected.
    const err = startImpersonation({
      corporateId: impersonateTarget.corporate_id,
      roleWeight: impersonateRoleWeight,
      userRoles,
    });
    if (err) {
      showToast('error', err);
      return;
    }
    setImpersonating(true);
  }, [impersonateTarget, impersonateRoleWeight, userRoles, showToast]);

  /** Filtered role options for impersonate dialog */
  const filteredRoles = useMemo(() => impersonationRoles(userRoles, myRoleWeight), [userRoles, myRoleWeight]);

  // Bulk action bar
  const renderBulkBar = () => {
    if (selectedIds.length === 0) return null;
    return (
      <div className="dp-bulk-bar">
        <div className="dp-bulk-bar__left">
          <span className="dp-bulk-bar__count">
            {selectedIds.length} corporation{selectedIds.length !== 1 ? 's' : ''} selected
          </span>
          <button type="button" onClick={() => setSelectedIds([])} className="dp-bulk-bar__deselect">
            Deselect All
          </button>
        </div>
        <div className="dp-bulk-bar__right">
          <button type="button" onClick={handleBulkExport} className="dp-bulk-btn">
            <svg className="dp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
            Export
          </button>
        </div>
      </div>
    );
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================
  return (
    <>
    <NavigationV2 />
    <div className="nav2-main">
    <div className="ps-page">
      {/* Page-level toast notification */}
      <Toast toast={toast} onDismiss={dismissToast} />

      {/* ============================================================
          PAGE HEADER
          ============================================================ */}
      <div className="ps-page-header">
        <div className="ps-page-header__inner">
          <div className="ps-breadcrumb-row">
            <div className="ps-breadcrumb">
              <span className="ps-breadcrumb__text">Customers</span>
              <svg className="dp-icon-md ps-breadcrumb__separator" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
              <span className="ps-breadcrumb__current">Corporations</span>
            </div>
          </div>
          <h1 className="ps-page-title">Corporations</h1>
        </div>
      </div>

      {/* ============================================================
          CONTENT
          ============================================================ */}
      <div className="dp-content">
        <div className="dp-controls">
          <div className="dp-controls__left">
            <div className="dp-search">
              <svg className="dp-icon-md dp-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
              <input
                type="search"
                placeholder="Search corporations..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="dp-search__input"
                aria-label="Search corporations"
              />
            </div>
          </div>
          <div className="dp-controls__right">
            <ColumnVisibilityToggle columns={CORP_COLUMN_DEFS} visibleColumns={visibleColumns} onToggle={handleColumnToggle} />
            <button type="button" className="dp-btn dp-btn--secondary" onClick={handleExportAll} aria-label="Export corporations">
              <svg className="dp-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              Export
            </button>
            {addRights ? (
              <button
                type="button"
                className="dp-btn dp-btn--primary"
                onClick={() => setDrawerOpen(true)}
              >
                <svg className="dp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                New Corp
              </button>
            ) : null}
          </div>
        </div>
        <ActiveFilterBar filters={filters} columnLabels={columnLabels} onRemoveFilter={handleRemoveFilter} onClearAll={handleClearAllFilters} />
        {renderBulkBar()}
        {loading ? (
          <TableSkeleton />
        ) : (
          <CorporationsTable
            data={paginated}
            allData={data}
            visibleColumns={visibleColumns}
            sortColumn={sortColumn}
            sortOrder={sortOrder}
            onSort={handleSort}
            filters={filters}
            onFilterChange={handleFilterChange}
            selectedIds={selectedIds}
            onSelectRow={handleSelectRow}
            onSelectAll={handleSelectAll}
            onImpersonate={handleImpersonate}
          />
        )}
        {/* Pagination */}
        <Pagination page={safePage} totalPages={totalPages} total={totalFiltered} perPage={perPage} onPageChange={setPage} onPerPageChange={setPerPage} entityLabel="corporations" selectId="dp-crp-per-page" />
      </div>

      {/* Corporation Drawer (Add / Migrate) */}
      <CorporationDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSave={handleAddCorporation}
      />

      {/* Impersonate Dialog */}
      {impersonateTarget ? (
        <ImpersonateDialog
          subject={impersonateTarget.corporate_name}
          detail={impersonateTarget.campus_name}
          roles={filteredRoles}
          myRoleWeight={myRoleWeight}
          roleWeight={impersonateRoleWeight}
          onRoleChange={setImpersonateRoleWeight}
          busy={impersonating}
          onCancel={() => setImpersonateTarget(null)}
          onConfirm={handleConfirmImpersonate}
        />
      ) : null}

    </div>
    </div>
    </>
  );
};

export default CorporationsPage;
